<template>
  <section v-if="inline" class="qsch qsch--inline">
    <header class="qsch__header qsch__header--inline modal-header">
      <div class="qsch__header-copy">
        <h3>Schedule</h3>
        <p class="qsch__description">
          Set when each round becomes available and what should happen when no slot is active.
        </p>
      </div>
      <div class="qsch__header-actions">
        <span class="qsch__step-actions-label">Save actions</span>
        <button type="button" class="qsch__action qsch__action--primary qsch__action--inline-save" @click="handleSave">
          Save
        </button>
        <span class="qsch__step-status" :class="{
          'qsch__step-status--error': validationErrors.length > 0,
          'qsch__step-status--saved': Boolean(saveSuccess) && validationErrors.length === 0,
        }">
          {{ validationErrors.length > 0 ? 'Needs attention' : (saveSuccess ? 'Saved' : 'Unsaved') }}
        </span>
      </div>
    </header>

    <div class="qsch__scroll-body qsch__scroll-body--inline modal-body">
      <div v-if="draft.length === 0" class="qsch__empty">
        No slots configured. Add rounds in the Questions editor first.
      </div>

      <div class="qsch__timeline">
        <div
          v-for="(slot, rIdx) in draft"
          :key="slot.id"
          class="qsch__entry"
        >
          <div v-if="rIdx > 0" class="qsch__connector">
            <div class="qsch__connector-line" />
            <span class="qsch__connector-label">{{ getIntervalLabel(rIdx) }}</span>
            <div class="qsch__connector-line" />
          </div>

          <div class="qsch__round-card" :class="{ 'qsch__round-card--first': rIdx === 0 }">
            <div class="qsch__round-card-top">
              <div class="qsch__round-dot" :class="{ 'qsch__round-dot--first': rIdx === 0 }" />
              <div class="qsch__round-info">
                <span class="qsch__round-num">Slot {{ rIdx + 1 }}</span>
                <input
                  type="text"
                  class="qsch__round-name"
                  :value="slot.label"
                  maxlength="40"
                  :aria-label="`Slot ${rIdx + 1} label`"
                  @input="slot.label = ($event.target as HTMLInputElement).value"
                >
                <span class="qsch__slot-round-ref">{{ getRoundLabel(slot.roundId) }}</span>
              </div>
              <span class="qsch__q-count">
                {{ getRoundQuestionCount(slot.roundId) }} Q
              </span>
            </div>

            <div class="qsch__release-row">
              <span class="qsch__release-label">Releases</span>
              <div class="qsch__release-controls">
                <select
                  class="qsch__select"
                  :value="slot.releaseType"
                  :disabled="rIdx === 0"
                  @change="setReleaseType(rIdx, ($event.target as HTMLSelectElement).value)"
                >
                  <option value="immediate">At campaign start</option>
                  <option value="offset">After N days</option>
                  <option value="date">On specific date</option>
                </select>

                <template v-if="slot.releaseType === 'offset'">
                  <input
                    type="number"
                    class="qsch__offset-input"
                    min="1"
                    max="365"
                    :value="slot.offsetDays ?? 7"
                    @input="slot.offsetDays = Number(($event.target as HTMLInputElement).value)"
                  >
                  <span class="qsch__unit">days</span>
                  <span class="qsch__unit">at</span>
                  <input
                    type="time"
                    class="qsch__time-input"
                    :value="slot.releaseTime ?? ''"
                    :aria-label="`Slot ${rIdx + 1} release time`"
                    @input="slot.releaseTime = ($event.target as HTMLInputElement).value"
                  >
                </template>

                <template v-if="slot.releaseType === 'date'">
                  <input
                    type="date"
                    class="qsch__date-input"
                    :value="slot.releaseDate ?? ''"
                    @input="slot.releaseDate = ($event.target as HTMLInputElement).value"
                  >
                  <input
                    type="time"
                    class="qsch__time-input"
                    :value="slot.releaseTime ?? ''"
                    :aria-label="`Slot ${rIdx + 1} release time`"
                    @input="slot.releaseTime = ($event.target as HTMLInputElement).value"
                  >
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p class="qsch__hint">
        Add or remove rounds in <strong>Questions</strong>. Schedule controls when each round is released.
      </p>

      <section class="qsch__fallback">
        <div class="qsch__fallback-copy">
          <h4>No Active Slot</h4>
          <p>Choose what should happen when no slot is active and there is no unfinished session to resume.</p>
        </div>

        <div class="qsch__preset-row">
          <button type="button" class="qsch__preset-btn" @click="applyFallbackPreset('redirect', '/home')">
            Redirect home
          </button>
          <button type="button" class="qsch__preset-btn" @click="applyFallbackPreset('holding-page', 'holding-page')">
            Holding page
          </button>
          <button type="button" class="qsch__preset-btn" @click="applyFallbackPreset('resume-last-available')">
            Resume last available
          </button>
        </div>

        <label class="qsch__field" for="qsch-no-active-kind-inline">
          Behaviour
          <select id="qsch-no-active-kind-inline" v-model="fallbackKind" class="qsch__select">
            <option value="">No special action</option>
            <option value="redirect">Redirect</option>
            <option value="holding-page">Holding page</option>
            <option value="not-available-message">Not available message</option>
            <option value="resume-last-available">Resume last available</option>
          </select>
        </label>

        <label v-if="fallbackNeedsValue" class="qsch__field" for="qsch-no-active-value-inline">
          {{ fallbackValueLabel }}
          <input
            id="qsch-no-active-value-inline"
            v-model="fallbackValue"
            type="text"
            class="qsch__text-input"
            :placeholder="fallbackValuePlaceholder"
          >
        </label>

        <p v-if="fallbackDescription" class="qsch__fallback-note">
          {{ fallbackDescription }}
        </p>
      </section>

      <ul v-if="validationErrors.length > 0" class="qsch__errors" aria-live="polite">
        <li v-for="error in validationErrors" :key="error">
          {{ error }}
        </li>
      </ul>

      <p v-else-if="saveSuccess" class="qsch__success">
        {{ saveSuccess }}
      </p>
    </div>

  </section>

  <teleport v-else to="body">
    <div
      v-if="show"
      class="qsch-overlay asset-action-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Distribution Schedule"
      @click.self="emit('close')"
    >
      <div class="qsch asset-action-modal">
        <header class="qsch__header modal-header">
          <div class="qsch__header-copy">
            <h3>Distribution Schedule</h3>
            <p class="qsch__description">
              Set when each round of questions becomes available to players.
              Round 1 always releases at campaign start.
            </p>
          </div>
          <button
            type="button"
            class="qsch__close modal-close-button"
            aria-label="Close schedule editor"
            @click="emit('close')"
          >
            &times;
          </button>
        </header>

        <div class="qsch__scroll-body modal-body">
          <div v-if="draft.length === 0" class="qsch__empty">
            No slots configured. Add rounds in the Questions editor first.
          </div>

          <div class="qsch__timeline">
            <div
              v-for="(slot, rIdx) in draft"
              :key="slot.id"
              class="qsch__entry"
            >
              <div v-if="rIdx > 0" class="qsch__connector">
                <div class="qsch__connector-line" />
                <span class="qsch__connector-label">{{ getIntervalLabel(rIdx) }}</span>
                <div class="qsch__connector-line" />
              </div>

              <div class="qsch__round-card" :class="{ 'qsch__round-card--first': rIdx === 0 }">
                <div class="qsch__round-card-top">
                  <div class="qsch__round-dot" :class="{ 'qsch__round-dot--first': rIdx === 0 }" />
                  <div class="qsch__round-info">
                    <span class="qsch__round-num">Slot {{ rIdx + 1 }}</span>
                    <input
                      type="text"
                      class="qsch__round-name"
                      :value="slot.label"
                      maxlength="40"
                      :aria-label="`Slot ${rIdx + 1} label`"
                      @input="slot.label = ($event.target as HTMLInputElement).value"
                    >
                    <span class="qsch__slot-round-ref">{{ getRoundLabel(slot.roundId) }}</span>
                  </div>
                  <span class="qsch__q-count">
                    {{ getRoundQuestionCount(slot.roundId) }} Q
                  </span>
                </div>

                <div class="qsch__release-row">
                  <span class="qsch__release-label">Releases</span>
                  <div class="qsch__release-controls">
                    <select
                      class="qsch__select"
                      :value="slot.releaseType"
                      :disabled="rIdx === 0"
                      @change="setReleaseType(rIdx, ($event.target as HTMLSelectElement).value)"
                    >
                      <option value="immediate">At campaign start</option>
                      <option value="offset">After N days</option>
                      <option value="date">On specific date</option>
                    </select>

                    <template v-if="slot.releaseType === 'offset'">
                      <input
                        type="number"
                        class="qsch__offset-input"
                        min="1"
                        max="365"
                        :value="slot.offsetDays ?? 7"
                        @input="slot.offsetDays = Number(($event.target as HTMLInputElement).value)"
                      >
                      <span class="qsch__unit">days</span>
                      <span class="qsch__unit">at</span>
                      <input
                        type="time"
                        class="qsch__time-input"
                        :value="slot.releaseTime ?? ''"
                        :aria-label="`Slot ${rIdx + 1} release time`"
                        @input="slot.releaseTime = ($event.target as HTMLInputElement).value"
                      >
                    </template>

                    <template v-if="slot.releaseType === 'date'">
                      <input
                        type="date"
                        class="qsch__date-input"
                        :value="slot.releaseDate ?? ''"
                        @input="slot.releaseDate = ($event.target as HTMLInputElement).value"
                      >
                      <input
                        type="time"
                        class="qsch__time-input"
                        :value="slot.releaseTime ?? ''"
                        :aria-label="`Slot ${rIdx + 1} release time`"
                        @input="slot.releaseTime = ($event.target as HTMLInputElement).value"
                      >
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p class="qsch__hint">
            Add or remove rounds in the <strong>Questions</strong> editor. Slots control when each round is released.
          </p>

          <section class="qsch__fallback">
            <div class="qsch__fallback-copy">
              <h4>No Active Slot</h4>
              <p>Choose what should happen when no slot is active and there is no unfinished session to resume.</p>
            </div>

            <div class="qsch__preset-row">
              <button type="button" class="qsch__preset-btn" @click="applyFallbackPreset('redirect', '/home')">
                Redirect home
              </button>
              <button type="button" class="qsch__preset-btn" @click="applyFallbackPreset('holding-page', 'holding-page')">
                Holding page
              </button>
              <button type="button" class="qsch__preset-btn" @click="applyFallbackPreset('resume-last-available')">
                Resume last available
              </button>
            </div>

            <label class="qsch__field" for="qsch-no-active-kind">
              Behaviour
              <select id="qsch-no-active-kind" v-model="fallbackKind" class="qsch__select">
                <option value="">No special action</option>
                <option value="redirect">Redirect</option>
                <option value="holding-page">Holding page</option>
                <option value="not-available-message">Not available message</option>
                <option value="resume-last-available">Resume last available</option>
              </select>
            </label>

            <label v-if="fallbackNeedsValue" class="qsch__field" for="qsch-no-active-value">
              {{ fallbackValueLabel }}
              <input
                id="qsch-no-active-value"
                v-model="fallbackValue"
                type="text"
                class="qsch__text-input"
                :placeholder="fallbackValuePlaceholder"
              >
            </label>

            <p v-if="fallbackDescription" class="qsch__fallback-note">
              {{ fallbackDescription }}
            </p>
          </section>

          <ul v-if="validationErrors.length > 0" class="qsch__errors" aria-live="polite">
            <li v-for="error in validationErrors" :key="error">
              {{ error }}
            </li>
          </ul>
        </div>

        <footer class="qsch__footer modal-footer">
          <div class="qsch__actions">
            <button type="button" class="qsch__action qsch__action--secondary" @click="emit('close')">
              Cancel
            </button>
            <button type="button" class="qsch__action qsch__action--primary" @click="handleSave">
              Save Schedule
            </button>
          </div>
        </footer>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import type { QuizNoActiveSlotAction, QuizNoActiveSlotActionKind, QuizRound, QuizSlot } from '@/types/form.types'

export interface QuizCampaignScheduleInput {
  startDateTime: string | Date | null
  endDateTime: string | Date | null
  timezone?: string | null
}

const props = defineProps<{
  show: boolean
  inline?: boolean
  slots: QuizSlot[]
  rounds: QuizRound[]
  campaignSchedule: QuizCampaignScheduleInput
  noActiveSlotAction: QuizNoActiveSlotAction | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'save', value: { slots: QuizSlot[]; noActiveSlotAction: QuizNoActiveSlotAction | null }): void
}>()

const draft = reactive<QuizSlot[]>([])
const fallbackKind = ref<QuizNoActiveSlotActionKind | ''>('')
const fallbackValue = ref('')
const validationErrors = ref<string[]>([])
const saveSuccess = ref('')

function deepCloneSlots(slots: QuizSlot[] = []): QuizSlot[] {
  return slots.map(slot => ({ ...slot }))
}

function buildDefaultSlotForRound(round: QuizRound, index: number): QuizSlot {
  return {
    id: `slot-${round.id || `round-${index + 1}`}`,
    label: round.label || `Round ${index + 1}`,
    roundId: round.id || `round-${index + 1}`,
    releaseType: index === 0 ? 'immediate' : 'offset',
    offsetDays: index === 0 ? undefined : index * 7,
  }
}

function reconcileSlotsWithRounds(rounds: QuizRound[], slots: QuizSlot[] = []): QuizSlot[] {
  const slotByRoundId = new Map(slots.map((slot) => [slot.roundId, { ...slot }]))
  return rounds.map((round, index) => {
    const existing = slotByRoundId.get(round.id)
    if (existing) {
      return {
        ...existing,
        id: existing.id || `slot-${round.id}`,
        label: existing.label || round.label || `Round ${index + 1}`,
        roundId: round.id,
      }
    }
    return buildDefaultSlotForRound(round, index)
  })
}

watch(() => [props.show, props.inline, props.slots, props.rounds, props.noActiveSlotAction] as const, ([visible, inline]) => {
  if (!visible && !inline) return
  draft.splice(0, draft.length, ...deepCloneSlots(reconcileSlotsWithRounds(props.rounds, props.slots)))
  fallbackKind.value = props.noActiveSlotAction?.kind ?? ''
  fallbackValue.value = props.noActiveSlotAction?.value ?? ''
  validationErrors.value = []
  if (!inline) {
    saveSuccess.value = ''
  }
}, { immediate: true, deep: true })

function setReleaseType(rIdx: number, type: string) {
  saveSuccess.value = ''
  const slot = draft[rIdx]
  if (!slot) return
  slot.releaseType = type as QuizSlot['releaseType']
  if (type === 'offset' && !slot.offsetDays) {
    slot.offsetDays = 7
  }
  if (type !== 'offset') {
    delete slot.offsetDays
  }
  if (type !== 'date') {
    delete slot.releaseDate
  }
  if (type === 'immediate') {
    delete slot.releaseTime
  }
}

function getIntervalLabel(rIdx: number): string {
  const prev = draft[rIdx - 1]
  const curr = draft[rIdx]
  if (!prev || !curr) return ''

  if (curr.releaseType === 'immediate') return 'Immediate'
  if (curr.releaseType === 'date') {
    if (!curr.releaseDate) return 'On date'
    return curr.releaseTime ? `→ ${curr.releaseDate} ${curr.releaseTime}` : `→ ${curr.releaseDate}`
  }
  if (curr.releaseType === 'offset') {
    const prevOffset = prev.releaseType === 'offset' ? (prev.offsetDays ?? 0) : 0
    const currOffset = curr.offsetDays ?? 0
    const gap = currOffset - prevOffset
    if (gap > 0) return `+ ${gap} day${gap !== 1 ? 's' : ''}`
    return `day ${currOffset}`
  }
  return '↓'
}

const fallbackNeedsValue = computed(() =>
  fallbackKind.value === 'redirect'
  || fallbackKind.value === 'holding-page'
  || fallbackKind.value === 'not-available-message',
)

const fallbackValueLabel = computed(() => {
  if (fallbackKind.value === 'not-available-message') return 'Message'
  return 'Value'
})

const fallbackValuePlaceholder = computed(() => {
  if (fallbackKind.value === 'redirect') return '/home or https://example.com'
  if (fallbackKind.value === 'holding-page') return 'holding-page-action'
  if (fallbackKind.value === 'not-available-message') return 'Check back soon.'
  return ''
})

const fallbackDescription = computed(() => {
  if (fallbackKind.value === 'redirect') return 'Use an internal route like /home or a full external URL.'
  if (fallbackKind.value === 'holding-page') return 'Provide the platform action or route that shows your holding experience.'
  if (fallbackKind.value === 'not-available-message') return 'The message should explain when the next slot opens.'
  if (fallbackKind.value === 'resume-last-available') return 'If an unfinished session exists, users resume it even after the slot closes.'
  return ''
})

function applyFallbackPreset(kind: QuizNoActiveSlotActionKind, value = '') {
  saveSuccess.value = ''
  fallbackKind.value = kind
  fallbackValue.value = value
}

function normalizeTimeZone(timeZone?: string | null): string {
  try {
    if (timeZone) {
      Intl.DateTimeFormat('en-US', { timeZone }).format(new Date())
      return timeZone
    }
  } catch (_error) {
    return 'UTC'
  }
  return 'UTC'
}

function getTimeZoneParts(date: Date, timeZone: string) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date).reduce<Record<string, number>>((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = Number(part.value)
    }
    return acc
  }, {})
}

function getTimeZoneOffsetMs(date: Date, timeZone: string): number {
  const parts = getTimeZoneParts(date, timeZone)
  return Date.UTC(
    parts.year,
    (parts.month || 1) - 1,
    parts.day || 1,
    parts.hour || 0,
    parts.minute || 0,
    parts.second || 0,
    0,
  ) - date.getTime()
}

function zonedDateTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  timeZone: string,
): Date {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second, 0)
  const initialOffset = getTimeZoneOffsetMs(new Date(utcGuess), timeZone)
  let timestamp = utcGuess - initialOffset
  const recalculatedOffset = getTimeZoneOffsetMs(new Date(timestamp), timeZone)
  if (recalculatedOffset !== initialOffset) {
    timestamp = utcGuess - recalculatedOffset
  }
  return new Date(timestamp)
}

function parseScheduleDate(value: string, timeZone: string, options?: { endOfDay?: boolean }): Date {
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value)
  const hasExplicitTimeZone = /(Z|[+-]\d{2}:\d{2})$/i.test(value)

  if (isDateOnly) {
    const [year, month, day] = value.split('-').map(Number)
    return zonedDateTimeToUtc(
      year,
      month,
      day,
      options?.endOfDay ? 23 : 0,
      options?.endOfDay ? 59 : 0,
      options?.endOfDay ? 59 : 0,
      timeZone,
    )
  }

  if (value.includes('T') && !hasExplicitTimeZone) {
    const [datePart, timePart = '00:00:00'] = value.split('T')
    const [year, month, day] = datePart.split('-').map(Number)
    const [hour = '0', minute = '0', second = '0'] = timePart.split(':')
    return zonedDateTimeToUtc(year, month, day, Number(hour), Number(minute), Number(second), timeZone)
  }

  return new Date(value)
}

function addLocalDays(value: string, days: number, timeZone: string): Date {
  const source = parseScheduleDate(value, timeZone)
  const parts = getTimeZoneParts(source, timeZone)
  return zonedDateTimeToUtc(
    parts.year,
    parts.month,
    (parts.day || 1) + days,
    parts.hour || 0,
    parts.minute || 0,
    parts.second || 0,
    timeZone,
  )
}

function collectValidationErrors(): string[] {
  const errors: string[] = []
  const timeZone = normalizeTimeZone(props.campaignSchedule.timezone)
  const startDateTime = props.campaignSchedule.startDateTime
    ? new Date(props.campaignSchedule.startDateTime).toISOString()
    : null
  const endDateTime = props.campaignSchedule.endDateTime
    ? new Date(props.campaignSchedule.endDateTime).toISOString()
    : null
  const hasCampaignWindow = Boolean(startDateTime && endDateTime)
  const campaignStart = hasCampaignWindow ? parseScheduleDate(startDateTime!, timeZone) : null
  const campaignEnd = hasCampaignWindow ? parseScheduleDate(endDateTime!, timeZone, { endOfDay: true }) : null

  const slotStarts = draft.map((slot, index) => {
    if (!slot.roundId || !props.rounds.some((round) => round.id === slot.roundId)) {
      errors.push(`Slot ${index + 1} is not linked to a valid round.`)
    }

    if (!slot.label.trim()) {
      errors.push(`Slot ${index + 1} must have a label.`)
    }

    if (slot.releaseType === 'date') {
      if (!slot.releaseDate) {
        errors.push(`Slot ${index + 1} needs a release date.`)
        return campaignStart ?? new Date(0)
      }
      const dateValue = slot.releaseTime
        ? `${slot.releaseDate}T${slot.releaseTime}`
        : slot.releaseDate
      return parseScheduleDate(dateValue, timeZone)
    }

    if (slot.releaseType === 'offset') {
      if (slot.offsetDays === undefined || !Number.isFinite(slot.offsetDays) || slot.offsetDays < 1 || slot.offsetDays > 365) {
        errors.push(`Slot ${index + 1} offset must be between 1 and 365 days.`)
        return campaignStart ?? new Date(0)
      }
      if (!startDateTime) {
        return new Date(slot.offsetDays * 24 * 60 * 60 * 1000)
      }
      const offsetDate = addLocalDays(startDateTime, slot.offsetDays, timeZone)
      if (slot.releaseTime) {
        const parts = getTimeZoneParts(offsetDate, timeZone)
        const [hour = '0', minute = '0'] = slot.releaseTime.split(':')
        return zonedDateTimeToUtc(parts.year, parts.month, parts.day || 1, Number(hour), Number(minute), 0, timeZone)
      }
      return offsetDate
    }

    return campaignStart ?? new Date(0)
  })

  if (campaignStart && campaignEnd) {
    slotStarts.forEach((start, index) => {
      if (start.getTime() < campaignStart.getTime()) {
        errors.push(`Slot ${index + 1} cannot start before the campaign starts.`)
      }
      if (start.getTime() > campaignEnd.getTime()) {
        errors.push(`Slot ${index + 1} cannot start after the campaign ends.`)
      }
    })
  }

  for (let index = 1; index < slotStarts.length; index += 1) {
    if (slotStarts[index].getTime() <= slotStarts[index - 1].getTime()) {
      errors.push(`Slot ${index + 1} must start after Slot ${index}.`)
    }
  }

  if (fallbackKind.value && fallbackNeedsValue.value && !fallbackValue.value.trim()) {
    errors.push('Provide a value for the no active slot behaviour.')
  }

  if (fallbackKind.value === 'redirect' && fallbackValue.value.trim()) {
    const trimmed = fallbackValue.value.trim()
    const validRedirect = trimmed.startsWith('/') || /^https?:\/\//i.test(trimmed)
    if (!validRedirect) {
      errors.push('Redirect behaviour must use an internal route or a full http/https URL.')
    }
  }

  return Array.from(new Set(errors))
}

function handleSave() {
  validationErrors.value = collectValidationErrors()
  if (validationErrors.value.length > 0) {
    saveSuccess.value = ''
    return
  }

  saveSuccess.value = props.inline
    ? 'Schedule saved. Next: review the Map to confirm the timeline and fallback behaviour.'
    : ''
  emit('save', {
    slots: deepCloneSlots(draft),
    noActiveSlotAction: fallbackKind.value
      ? {
          kind: fallbackKind.value,
          ...(fallbackValue.value.trim() ? { value: fallbackValue.value.trim() } : {}),
        }
      : null,
  })
}

function getRoundLabel(roundId: string): string {
  return props.rounds.find((round) => round.id === roundId)?.label ?? roundId
}

function getRoundQuestionCount(roundId: string): number {
  const round = props.rounds.find((candidate) => candidate.id === roundId)
  if (!round) return 0
  return round.stages.reduce((sum, stage) => sum + stage.questions.length, 0)
}
</script>

<style scoped>
.qsch-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  z-index: calc(var(--base-layout-modal-z-index) + 200);
}

.qsch {
  --qsch-scale: 1;
  width: min(580px, 100%);
  max-height: min(92vh, 780px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.qsch--inline {
  width: 100%;
  min-width: 100%;
  max-height: none;
  border: 0;
  border-radius: 0;
  background: transparent;
  padding: 0;
  box-shadow: none;
}

.qsch--inline .qsch__header h3,
.qsch--inline .qsch__description,
.qsch--inline .qsch__empty,
.qsch--inline .qsch__connector-label,
.qsch--inline .qsch__round-num,
.qsch--inline .qsch__slot-round-ref,
.qsch--inline .qsch__q-count,
.qsch--inline .qsch__release-label,
.qsch--inline .qsch__unit,
.qsch--inline .qsch__hint,
.qsch--inline .qsch__fallback-copy p,
.qsch--inline .qsch__field,
.qsch--inline .qsch__fallback-note {
  color: rgba(237, 244, 255, 0.72);
}

.qsch--inline .qsch__fallback-copy h4,
.qsch--inline .qsch__round-name,
.qsch--inline .qsch__preset-btn {
  color: #f5fbff;
}

.qsch--inline .qsch__scroll-body,
.qsch--inline .qsch__timeline,
.qsch--inline .qsch__entry {
  width: 100%;
  max-width: none;
}

.qsch__header--inline {
  padding: 1rem 1rem 0.75rem;
}

.qsch__action--inline-save {
  width: 100%;
  min-width: 0;
  min-height: 1.7rem;
  padding: 0.24rem 0.6rem;
  font-size: 0.76rem;
}

.qsch__header-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.35rem;
  margin-left: auto;
  width: 9.5rem;
  min-height: 5.55rem;
  padding: 0.5rem 0.55rem;
  border: 1px solid rgba(122, 230, 177, 0.12);
  border-radius: 0.85rem;
  background: rgba(255, 255, 255, 0.04);
  box-sizing: border-box;
}

.qsch__step-actions-label {
  width: 100%;
  color: #f5fbff;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.qsch__step-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 1.65rem;
  padding: 0.22rem 0.55rem;
  border: 1px solid rgba(122, 230, 177, 0.16);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(237, 244, 255, 0.86);
  font-size: 0.74rem;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  white-space: nowrap;
}

.qsch__step-status--saved {
  border-color: rgba(122, 230, 177, 0.3);
  background: rgba(122, 230, 177, 0.12);
  color: rgba(122, 230, 177, 0.96);
}

.qsch__step-status--error {
  border-color: rgba(255, 152, 173, 0.28);
  background: rgba(255, 152, 173, 0.12);
  color: #ff98ad;
}

.qsch__scroll-body--inline {
  overflow: visible;
  padding: 1rem;
}

@media (max-width: 900px) {
  .qsch__header--inline {
    align-items: flex-start;
  }

  .qsch__header-actions {
    margin-left: 0;
    width: 100%;
  }
}

.qsch--inline .qsch__connector-line,
.qsch--inline .qsch__tabs,
.qsch--inline .qsch__fallback {
  border-color: rgba(122, 230, 177, 0.1);
}

.qsch--inline .qsch__connector-label,
.qsch--inline .qsch__q-count,
.qsch--inline .qsch__round-card,
.qsch--inline .qsch__preset-btn {
  border-color: rgba(122, 230, 177, 0.12);
  background: rgba(255, 255, 255, 0.04);
}

.qsch--inline .qsch__round-card--first {
  border-color: rgba(122, 230, 177, 0.38);
  background: rgba(122, 230, 177, 0.14);
}

.qsch--inline .qsch__select,
.qsch--inline .qsch__offset-input,
.qsch--inline .qsch__date-input,
.qsch--inline .qsch__time-input,
.qsch--inline .qsch__text-input {
  color: #f5fbff;
  background: rgba(5, 13, 24, 0.52);
  border-color: rgba(122, 230, 177, 0.12);
}

.qsch--inline .qsch__text-input::placeholder {
  color: rgba(237, 244, 255, 0.45);
}

.qsch--inline .qsch__action--primary {
  border-color: rgba(122, 230, 177, 0.96);
  background: rgba(122, 230, 177, 0.96);
  color: #06111b;
}

.qsch--inline .qsch__action--primary:hover {
  border-color: rgba(122, 230, 177, 0.82);
  background: rgba(122, 230, 177, 0.82);
}

.qsch__footer--inline {
  padding: 0 1rem 1rem;
}

.qsch__actions--inline {
  justify-content: flex-start;
}

.qsch__actions--inline > button {
  flex: 0 0 auto;
  min-width: 10rem;
}

.qsch__success {
  margin: 1rem 0 0;
  padding: 0.8rem 0.95rem;
  border-radius: 12px;
  background: rgba(57, 255, 156, 0.12);
  border: 1px solid rgba(57, 255, 156, 0.24);
  color: #d8fff0;
  font-size: 0.85rem;
}

.qsch__header {
  flex-shrink: 0;
}

.qsch__header-copy {
  display: grid;
  gap: 0.35rem;
  max-width: min(100%, 26rem);
  text-align: left;
  justify-items: start;
  align-self: flex-start;
}

.qsch__header h3 {
  margin-bottom: 0;
}

.qsch__description {
  margin: 0;
  color: var(--ui-inverse-text-muted);
  font-size: 0.92rem;
  line-height: 1.45;
}

.qsch__scroll-body {
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 1rem 1.25rem 1.25rem;
}

.qsch__empty {
  text-align: center;
  color: var(--ui-inverse-text-muted);
  font-size: 0.9rem;
  padding: 2rem 0;
}

.qsch__timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.qsch__entry {
  display: flex;
  flex-direction: column;
}

.qsch__connector {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  padding: 0.15rem 0;
  margin-left: 0.85rem;
}

.qsch__connector-line {
  width: 2px;
  height: 1rem;
  background: var(--ui-inverse-border);
  border-radius: 999px;
}

.qsch__connector-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--ui-inverse-text-muted);
  background: rgba(16, 26, 52, 0.08);
  border: 1px solid var(--ui-inverse-border);
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  white-space: nowrap;
}

.qsch__round-card {
  border: 1px solid var(--ui-inverse-border);
  border-radius: 0.9rem;
  padding: 0.85rem 1rem;
  background: rgba(230, 255, 244, 0.12);
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  transition: border-color 150ms ease;
}

.qsch__round-card--first {
  border-color: var(--ui-inverse-cta-bg);
  background: rgba(230, 255, 244, 0.18);
}

.qsch__round-card-top {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.qsch__round-dot {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  flex-shrink: 0;
  background: var(--ui-inverse-border-strong);
  border: 2px solid var(--ui-inverse-border);
}

.qsch__round-dot--first {
  background: var(--ui-inverse-cta-bg);
  border-color: var(--ui-inverse-cta-bg);
}

.qsch__round-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.qsch__round-num {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ui-inverse-text-muted);
}

.qsch__round-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--ui-inverse-text);
  background: transparent;
  border: none;
  border-bottom: 1px solid transparent;
  border-radius: 0;
  padding: 0;
  min-height: unset;
  width: 100%;
  transition: border-color 150ms ease;
}

.qsch__round-name:focus {
  outline: none;
  border-bottom-color: var(--ui-inverse-cta-bg);
}

.qsch__slot-round-ref {
  font-size: 0.75rem;
  color: var(--ui-inverse-text-muted);
}

.qsch__q-count {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--ui-inverse-text-muted);
  background: rgba(16, 26, 52, 0.1);
  border-radius: 999px;
  padding: 0.2rem 0.55rem;
  flex-shrink: 0;
}

.qsch__release-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
}

.qsch__release-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ui-inverse-text-muted);
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 4rem;
}

.qsch__release-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  flex: 1;
}

.qsch__select,
.qsch__offset-input,
.qsch__date-input,
.qsch__time-input,
.qsch__text-input {
  min-height: unset;
  border: 1px solid rgba(16, 26, 52, 0.18);
  background: rgba(16, 26, 52, 0.08);
  color: var(--ui-inverse-text);
}

.qsch__select {
  height: 2.1rem;
  border-radius: 0.6rem;
  padding: 0 0.65rem;
  font-size: 0.82rem;
}

.qsch__select:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.qsch__offset-input {
  width: 4rem;
  height: 2.1rem;
  border-radius: 0.6rem;
  padding: 0 0.5rem;
  font-size: 0.82rem;
  text-align: center;
}

.qsch__date-input {
  height: 2.1rem;
  border-radius: 0.6rem;
  padding: 0 0.65rem;
  font-size: 0.82rem;
}

.qsch__time-input {
  height: 2.1rem;
  border-radius: 0.6rem;
  padding: 0 0.5rem;
  font-size: 0.82rem;
}

.qsch__unit {
  font-size: 0.78rem;
  color: var(--ui-inverse-text-muted);
  white-space: nowrap;
}

.qsch__hint {
  margin: 1.1rem 0 0;
  font-size: 0.8rem;
  color: var(--ui-inverse-text-muted);
  text-align: center;
  opacity: 0.7;
}

.qsch__fallback {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: grid;
  gap: 0.85rem;
}

.qsch__preset-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.qsch__preset-btn {
  min-height: unset;
  height: auto;
  padding: 0.45rem 0.7rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  color: var(--ui-inverse-text, #fff);
  font-size: 0.78rem;
  font-weight: 600;
  box-shadow: none;
}

.qsch__preset-btn:hover {
  background: rgba(255, 255, 255, 0.12);
}

.qsch__fallback-copy h4 {
  margin: 0;
  font-size: 0.95rem;
}

.qsch__fallback-copy p {
  margin: 0.35rem 0 0;
  color: var(--ui-inverse-text-muted);
  font-size: 0.84rem;
  line-height: 1.45;
}

.qsch__field {
  display: grid;
  gap: 0.4rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--ui-inverse-text, #fff);
}

.qsch__fallback-note {
  margin: 0;
  color: var(--ui-inverse-text-muted);
  font-size: 0.8rem;
  line-height: 1.45;
}

.qsch__text-input {
  width: 100%;
  min-height: 2.75rem;
  border-radius: 0.85rem;
  padding: 0.7rem 0.85rem;
}

.qsch__errors {
  margin: 1rem 0 0;
  padding-left: 1.15rem;
  color: #ff9ba9;
  display: grid;
  gap: 0.35rem;
  font-size: 0.84rem;
}

.qsch__footer {
  flex-shrink: 0;
}

.qsch__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.6rem;
  width: 100%;
}

.qsch__actions > button {
  flex: 1 1 0;
}

.qsch__action {
  min-height: 2.85rem;
  margin: 0;
  border-radius: 999px;
  padding: 0.65rem 1rem;
  font-family: var(--ui-inverse-font-family);
  font-weight: 700;
  transition: background-color 140ms ease, border-color 140ms ease, color 140ms ease;
}

.qsch__action--secondary {
  border: 1px solid var(--ui-inverse-border-strong);
  background: transparent;
  color: var(--ui-inverse-text);
}

.qsch__action--secondary:hover {
  background: rgba(16, 26, 52, 0.08);
  border-color: rgba(16, 26, 52, 0.36);
}

.qsch__action--primary {
  border: 1px solid var(--ui-inverse-cta-bg);
  background: var(--ui-inverse-cta-bg);
  color: var(--ui-inverse-cta-text);
}

.qsch__action--primary:hover {
  background: var(--ui-inverse-cta-bg-hover);
  border-color: var(--ui-inverse-cta-bg-hover);
}

.qsch__header-actions .qsch__action--inline-save {
  min-height: 1.7rem !important;
  padding: 0.24rem 0.6rem !important;
  font-size: 0.76rem !important;
  text-align: center !important;
}

@media (min-width: 769px) {
  .qsch {
    --qsch-scale: calc(var(--desktop-modal-scale, 1) * var(--desktop-display-density-scale, 1));
    width: min(calc(580px * var(--qsch-scale)), 100%);
    max-height: min(92vh, calc(780px * var(--qsch-scale)));
  }

  .qsch__header {
    padding:
      max(1.1rem, calc(1.5rem * var(--qsch-scale)))
      max(2.2rem, calc(3.5rem * var(--qsch-scale)));
  }

  .qsch__scroll-body {
    padding:
      max(0.8rem, calc(1rem * var(--qsch-scale)))
      max(1rem, calc(1.25rem * var(--qsch-scale)))
      max(1rem, calc(1.25rem * var(--qsch-scale)));
  }

  .qsch__action {
    min-height: calc(2.85rem * var(--qsch-scale));
    padding: calc(0.65rem * var(--qsch-scale)) calc(1rem * var(--qsch-scale));
    font-size: calc(0.94rem * var(--qsch-scale));
  }
}

@media (min-width: 769px) and (max-height: 900px) {
  .qsch-overlay {
    align-items: flex-start;
    padding: 1.25rem 1rem;
  }
}

@media (max-width: 640px) {
  .qsch {
    max-height: min(94vh, 780px);
  }

  .qsch__header,
  .qsch__scroll-body,
  .qsch__actions {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .qsch__release-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.4rem;
  }
}
</style>
